const FAQ_ITEMS = [
  {
    id: 'lista-precios',
    question: '¿Qué es una lista de precios?',
    answer: 'Es el contenedor comercial donde agrupás una o más versiones tarifarias. La lista define el marco y la versión define desde cuándo corre cada conjunto de valores.',
    topics: ['listas', 'reglas'],
  },
  {
    id: 'version',
    question: '¿Qué es una versión?',
    answer: 'Es una foto vigente de una lista en un período determinado. Si cambiás valores o criterios, conviene crear una nueva versión para preservar historial y vigencias.',
    topics: ['listas', 'reglas'],
  },
  {
    id: 'codigo',
    question: '¿Qué código conviene usar?',
    answer: 'Conviene usar códigos cortos, estables y legibles. Ejemplos: GENERAL, CLIENTE_ACME, EXPRESO_2026, FLETE_KG. Evitá textos largos o ambiguos.',
    topics: ['conceptos', 'numeradores', 'listas', 'reglas'],
  },
  {
    id: 'conceptos',
    question: '¿Qué son los conceptos?',
    answer: 'Son los ítems valorizables que después aparecen en el detalle tarifario, como flete, seguro, reentrega o adicional. Las reglas calculan importes para esos conceptos.',
    topics: ['conceptos', 'reglas'],
  },
  {
    id: 'numeradores',
    question: '¿Para qué sirven los numeradores?',
    answer: 'Sirven para ordenar, segmentar o identificar circuitos comerciales. No calculan importes por sí solos, pero ayudan a diferenciar contextos dentro del tarifario.',
    topics: ['numeradores', 'listas', 'reglas'],
  },
  {
    id: 'regla-tarifaria',
    question: '¿Qué es una regla tarifaria?',
    answer: 'Es la instrucción que indica qué concepto calcular, con qué base, qué método y bajo qué filtros de aplicación. Es la unidad operativa que finalmente valoriza.',
    topics: ['reglas'],
  },
  {
    id: 'tarifa-general',
    question: '¿Cuándo usar tarifa general?',
    answer: 'Usala cuando la condición aplica a todo el universo sin distinguir cliente, servicio o sucursal. Después podés superponer reglas más específicas para excepciones puntuales.',
    topics: ['listas', 'reglas'],
  },
  {
    id: 'tarifa-cliente',
    question: '¿Cuándo usar tarifa por cliente?',
    answer: 'Usala cuando un cliente necesita condiciones particulares. En la práctica, una regla con cliente específico suele imponerse sobre una general equivalente.',
    topics: ['listas', 'reglas'],
  },
  {
    id: 'varias-reglas',
    question: '¿Qué pasa si hay varias reglas que coinciden?',
    answer: 'El motor compara especificidad y, si hace falta, prioridad. Por eso conviene evitar reglas casi iguales sin una diferencia clara de alcance o prioridad.',
    topics: ['reglas'],
  },
  {
    id: 'vigencia',
    question: '¿Qué significa vigencia?',
    answer: 'Define desde cuándo y hasta cuándo puede usarse una versión. Sin vigencia consistente, una tarifa puede quedar fuera del circuito operativo aunque esté bien cargada.',
    topics: ['listas', 'reglas'],
  },
  {
    id: 'importe-minimo',
    question: '¿Qué es el importe mínimo?',
    answer: 'Es el piso de facturación para la regla. Si el cálculo principal da menos que ese valor, se respeta el mínimo cargado.',
    topics: ['reglas'],
  },
  {
    id: 'valor-declarado',
    question: '¿Cómo se calcula por valor declarado?',
    answer: 'Cuando la base es valor declarado y el método es porcentaje, el importe surge de aplicar ese porcentaje sobre el valor declarado de la guía.',
    topics: ['reglas'],
  },
  {
    id: 'no-aplica',
    question: '¿Por qué una tarifa puede no aplicar?',
    answer: 'Puede quedar afuera por falta de vigencia, versión inactiva, filtros demasiado restrictivos, cliente o servicio incorrecto, o porque otra regla más específica ganó antes.',
    topics: ['listas', 'reglas'],
  },
];

const normalizeString = (value) => String(value || '').trim();
const hasValue = (value) => normalizeString(value).length > 0;
const toNumber = (value) => Number(value || 0);

const getKind = (formState = {}) =>
  formState.assistantKind || formState.kind || formState.entityKind || 'reglas';

const push = (collection, condition, item) => {
  if (condition) collection.push(item);
};

function buildChecklist(kind, formState) {
  if (kind === 'conceptos') {
    return [
      { id: 'codigo', label: 'Código definido', done: hasValue(formState.codigo) },
      { id: 'nombre', label: 'Nombre claro', done: hasValue(formState.nombre) },
      { id: 'tipo', label: 'Tipo de concepto indicado', done: hasValue(formState.tipoConcepto) },
      { id: 'activo', label: 'Concepto activo', done: formState.activo !== false },
    ];
  }

  if (kind === 'numeradores') {
    return [
      { id: 'codigo', label: 'Código definido', done: hasValue(formState.codigo) },
      { id: 'nombre', label: 'Nombre identificado', done: hasValue(formState.nombre) },
      { id: 'secuencia', label: 'Siguiente número válido', done: toNumber(formState.siguienteNumero) > 0 },
      { id: 'activo', label: 'Numerador activo', done: formState.activo !== false },
    ];
  }

  if (kind === 'listas') {
    return [
      { id: 'codigo', label: 'Código de lista', done: hasValue(formState.codigo) },
      { id: 'nombre', label: 'Nombre comercial', done: hasValue(formState.nombre) },
      { id: 'version', label: 'Versión definida', done: hasValue(formState.versionNombre) },
      { id: 'vigencia', label: 'Vigencia desde definida', done: hasValue(formState.vigenciaDesde) },
      { id: 'activo', label: 'Lista y versión operativas', done: formState.activo !== false && normalizeString(formState.estado) !== 'INACTIVA' },
    ];
  }

  return [
    { id: 'version', label: 'Lista / versión seleccionada', done: hasValue(formState.versionId) },
    { id: 'concepto', label: 'Concepto seleccionado', done: hasValue(formState.conceptId) },
    { id: 'base', label: 'Base de cálculo definida', done: hasValue(formState.unidadBase) },
    { id: 'metodo', label: 'Método de cálculo definido', done: hasValue(formState.metodoCalculo) },
    {
      id: 'valor',
      label: 'Valor principal cargado',
      done: normalizeString(formState.metodoCalculo) === 'PORCENTAJE' ? toNumber(formState.porcentaje) > 0 : toNumber(formState.valorUnitario) > 0,
    },
    { id: 'alcance', label: 'Alcance revisado', done: hasValue(formState.clienteId) || hasValue(formState.servicioCodigo) || hasValue(formState.origenSucursalId) || hasValue(formState.destinoSucursalId) || hasValue(formState.origenLocalidadId) || hasValue(formState.destinoLocalidadId) || hasValue(formState.numeradorId) || true },
    { id: 'activo', label: 'Regla activa', done: formState.activo !== false },
  ];
}

function buildSummary(kind, formState) {
  if (kind === 'conceptos') {
    if (hasValue(formState.nombre)) return `Estás definiendo el concepto ${formState.nombre}.`;
    return 'Estás creando un concepto valorizable.';
  }

  if (kind === 'numeradores') {
    if (hasValue(formState.nombre)) return `Estás preparando el numerador ${formState.nombre}.`;
    return 'Estás creando un numerador para segmentar o identificar tarifas.';
  }

  if (kind === 'listas') {
    if (hasValue(formState.clienteId)) return 'Estás creando una lista orientada a un cliente específico.';
    if (hasValue(formState.servicioCodigo)) return 'Estás creando una lista enfocada en un servicio específico.';
    if (normalizeString(formState.alcance) === 'GENERAL' || !hasValue(formState.alcance)) return 'Estás creando una lista general.';
    return `Estás creando una lista con alcance ${normalizeString(formState.alcance).toLowerCase()}.`;
  }

  if (hasValue(formState.clienteId)) return 'Esta regla aplica a un cliente específico.';
  if (hasValue(formState.servicioCodigo)) return 'Esta regla aplica a un servicio específico.';
  if (hasValue(formState.origenSucursalId) || hasValue(formState.destinoSucursalId) || hasValue(formState.origenLocalidadId) || hasValue(formState.destinoLocalidadId)) {
    return 'Esta regla está acotada por origen y/o destino.';
  }
  return 'Estás creando una regla general.';
}

function buildNextSteps(kind, formState) {
  if (kind === 'conceptos') {
    return [
      !hasValue(formState.codigo) ? 'Definí un código corto y estable para poder reutilizar el concepto.' : null,
      !hasValue(formState.nombre) ? 'Completá un nombre entendible por operación y auditoría.' : null,
      hasValue(formState.codigo) && hasValue(formState.nombre) ? 'Cuando el concepto quede claro, seguí con numeradores y listas.' : null,
    ].filter(Boolean);
  }

  if (kind === 'numeradores') {
    return [
      !hasValue(formState.codigo) ? 'Definí un código identificable para el numerador.' : null,
      toNumber(formState.siguienteNumero) <= 0 ? 'Ajustá el siguiente número a un valor mayor que cero.' : null,
      hasValue(formState.codigo) && hasValue(formState.nombre) ? 'Con el numerador listo, ya podés asociarlo a listas o reglas que lo necesiten.' : null,
    ].filter(Boolean);
  }

  if (kind === 'listas') {
    return [
      !hasValue(formState.versionNombre) ? 'Asigná un nombre de versión para distinguir cambios de vigencia o valores.' : null,
      !hasValue(formState.vigenciaDesde) ? 'Definí la vigencia desde para que la versión pueda entrar al circuito operativo.' : null,
      hasValue(formState.versionNombre) && hasValue(formState.vigenciaDesde) ? 'Después de guardar la lista, avanzá con las reglas que vivirán dentro de esa versión.' : null,
    ].filter(Boolean);
  }

  return [
    !hasValue(formState.versionId) ? 'Seleccioná primero la lista / versión donde vivirá la regla.' : null,
    !hasValue(formState.conceptId) ? 'Elegí el concepto que querés valorizar.' : null,
    !hasValue(formState.unidadBase) || !hasValue(formState.metodoCalculo) ? 'Definí base y método para que la regla se lea como una instrucción de negocio.' : null,
    hasValue(formState.clienteId) ? 'Revisá si también necesitás acotar por servicio o sucursal, o si el cliente solo ya alcanza.' : 'Si la regla no es general, agregá filtros de cliente, servicio o ubicación.',
  ].filter(Boolean);
}

export function getTariffAssistantState(formState = {}) {
  const kind = getKind(formState);
  const checklist = buildChecklist(kind, formState);
  const completed = checklist.filter((item) => item.done).length;
  const total = checklist.length;

  return {
    kind,
    summary: buildSummary(kind, formState),
    badge: kind === 'reglas' ? 'Sugerencias automáticas' : 'Guía inteligente',
    checklist,
    progressLabel: `${completed}/${total} puntos revisados`,
    nextSteps: buildNextSteps(kind, formState),
    statusLabel: formState.id ? 'Edición' : 'Alta',
  };
}

export function getTariffAssistantSuggestions(formState = {}) {
  const kind = getKind(formState);
  const suggestions = [];

  if (!hasValue(formState.codigo)) {
    push(suggestions, true, 'Usá un código corto y estable, por ejemplo GENERAL, CLIENTE_ACME, EXPRESO_2026 o FLETE_KG.');
  }

  if (kind === 'listas') {
    push(suggestions, hasValue(formState.numeradorId), 'El numerador ayuda a ordenar o segmentar la lista dentro del circuito comercial.');
    push(suggestions, hasValue(formState.versionNombre), 'La versión te permite congelar condiciones y mantener historial sin sobrescribir reglas anteriores.');
  }

  if (kind === 'reglas') {
    push(suggestions, hasValue(formState.clienteId), 'Al quedar asociada a un cliente, esta regla normalmente tendrá preferencia frente a una general equivalente.');
    push(suggestions, hasValue(formState.servicioCodigo), 'Al limitar por servicio, la regla aplica solo para ese tipo de prestación.');
    push(suggestions, toNumber(formState.minimo) > 0, 'El importe mínimo funciona como piso de facturación para esta regla.');
    push(
      suggestions,
      normalizeString(formState.metodoCalculo) === 'PORCENTAJE' && normalizeString(formState.unidadBase) === 'VALOR_DECLARADO',
      'El porcentaje se calculará sobre el valor declarado de la guía.',
    );
    push(
      suggestions,
      !hasValue(formState.prioridad) || toNumber(formState.prioridad) === 100,
      'Si pueden convivir varias reglas parecidas, definí una prioridad explícita para evitar ambigüedad operativa.',
    );
    push(
      suggestions,
      !hasValue(formState.clienteId) && !hasValue(formState.servicioCodigo) && !hasValue(formState.origenSucursalId) && !hasValue(formState.destinoSucursalId) && !hasValue(formState.origenLocalidadId) && !hasValue(formState.destinoLocalidadId),
      'Sin filtros adicionales, la regla queda con alcance general dentro de la versión elegida.',
    );
  }

  if (kind === 'conceptos') {
    push(suggestions, hasValue(formState.tipoConcepto), 'Elegí un tipo de concepto consistente para que el detalle tarifario sea fácil de auditar.');
  }

  if (kind === 'numeradores') {
    push(suggestions, hasValue(formState.prefijo), 'El prefijo ayuda a reconocer rápidamente el circuito o familia del numerador.');
  }

  return suggestions.slice(0, 5);
}

export function getTariffAssistantWarnings(formState = {}) {
  const kind = getKind(formState);
  const warnings = [];

  if (kind === 'listas') {
    push(warnings, !hasValue(formState.vigenciaDesde), 'Sin vigencia desde, la tarifa puede no quedar disponible para emisión.');
    push(warnings, normalizeString(formState.estado) === 'INACTIVA' || formState.activo === false, 'Esta lista o versión está inactiva y no debería usarse en emisión.');
    push(
      warnings,
      hasValue(formState.vigenciaDesde) && hasValue(formState.vigenciaHasta) && normalizeString(formState.vigenciaHasta) < normalizeString(formState.vigenciaDesde),
      'La vigencia hasta no debería ser anterior a la vigencia desde.',
    );
  }

  if (kind === 'reglas') {
    push(warnings, !hasValue(formState.versionId), 'Falta definir la lista / versión donde vive la regla.');
    push(warnings, !hasValue(formState.conceptId), 'Falta seleccionar el concepto a valorizar.');
    push(warnings, !hasValue(formState.unidadBase) || !hasValue(formState.metodoCalculo), 'La regla todavía no tiene base o método de cálculo completos.');
    push(
      warnings,
      normalizeString(formState.metodoCalculo) === 'PORCENTAJE' && toNumber(formState.porcentaje) <= 0,
      'El porcentaje debe ser mayor a cero para que la regla tenga efecto.',
    );
    push(
      warnings,
      normalizeString(formState.metodoCalculo) !== 'PORCENTAJE' && toNumber(formState.valorUnitario) <= 0,
      'El valor unitario debe ser mayor a cero para que la regla tenga efecto.',
    );
    push(warnings, formState.activo === false, 'La regla está inactiva y no debería ser tomada por emisión.');
    push(
      warnings,
      hasValue(formState.origenSucursalId) && !hasValue(formState.origenLocalidadId),
      'Hay sucursal de origen pero no localidad de origen. Revisá si el filtro quedó a medio completar.',
    );
    push(
      warnings,
      hasValue(formState.destinoSucursalId) && !hasValue(formState.destinoLocalidadId),
      'Hay sucursal de destino pero no localidad de destino. Revisá si el filtro quedó a medio completar.',
    );
  }

  if (kind === 'conceptos') {
    push(warnings, !hasValue(formState.codigo) || !hasValue(formState.nombre), 'Conviene completar código y nombre antes de guardar el concepto.');
  }

  if (kind === 'numeradores') {
    push(warnings, toNumber(formState.siguienteNumero) <= 0, 'El siguiente número debería ser mayor a cero.');
  }

  return warnings.slice(0, 5);
}

export function getTariffAssistantFaq(context = {}) {
  const kind = typeof context === 'string' ? context : getKind(context);
  const items = FAQ_ITEMS.filter((item) => item.topics.includes(kind) || item.topics.includes('reglas'));
  return items.length ? items : FAQ_ITEMS;
}
